import { useMemo } from "react";
import { resolvePlanLimits, parsePlanContent } from "@/utils/plans";

interface PlanLimitPreviewProps {
  /** Live form values, re-parsed on every keystroke. */
  name: string;
  slug: string;
  tagline: string;
  /** Raw highlights textarea value (one highlight per line). */
  highlightsText: string;
}

/**
 * Live preview of what the business listing form will render for the plan the
 * admin is currently editing. Mirrors the same content-aware parser the public
 * form uses, so a mistyped highlight is obvious before saving.
 */
export default function PlanLimitPreview({
  name,
  slug,
  tagline,
  highlightsText,
}: PlanLimitPreviewProps) {
  const parsed = useMemo(() => {
    const highlights = highlightsText
      .split("\n")
      .map((h) => h.trim())
      .filter(Boolean);
    const plan = { slug, name, tagline, highlights };
    return { raw: parsePlanContent(plan), limits: resolvePlanLimits(plan) };
  }, [slug, name, tagline, highlightsText]);

  const { raw, limits } = parsed;

  const rows = [
    {
      icon: "ri-layout-grid-line",
      label: "Category dropdowns",
      value: `${limits.categories} ${
        limits.categories === 1 ? "dropdown" : "dropdowns"
      } (1 primary${limits.categories > 1 ? ` + ${limits.categories - 1} optional` : ""})`,
      on: true,
      stated: raw.categories != null,
    },
    {
      icon: "ri-node-tree",
      label: "Sub category dropdowns",
      value:
        limits.subcategories > 0
          ? `${limits.subcategories} ${limits.subcategories === 1 ? "dropdown" : "dropdowns"}`
          : "Hidden (no sub categories)",
      on: limits.subcategories > 0,
      stated: raw.subcategories != null,
    },
    {
      icon: "ri-image-line",
      label: "Photos allowed",
      value: `Up to ${limits.photos} ${limits.photos === 1 ? "photo" : "photos"}`,
      on: true,
      stated: raw.photos != null,
    },
    {
      icon: limits.video ? "ri-video-line" : "ri-video-off-line",
      label: "Video tour field",
      value: limits.video ? "Shown" : "Hidden",
      on: limits.video,
      stated: raw.video != null,
    },
  ];

  // Anything left to the slug default is worth nudging the admin about.
  const notStated = rows.filter((row) => !row.stated).map((row) => row.label.toLowerCase());

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/60 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary-800">
          <i className="ri-eye-line text-sm" />
          Live form preview
        </p>
        <span className="inline-flex items-center gap-1 rounded-full bg-background-50 px-2 py-0.5 text-[10px] font-semibold text-foreground-500">
          <i className="ri-magic-line text-[11px]" />
          Auto-read from your highlights
        </span>
      </div>

      <ul className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
        {rows.map((row) => (
          <li
            key={row.label}
            className="flex items-center gap-2.5 rounded-md border border-background-200 bg-background-50 px-3 py-2"
          >
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                row.on ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-400"
              }`}
            >
              <i className={`${row.icon} text-sm`} />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium uppercase tracking-wide text-foreground-500">
                {row.label}
              </p>
              <p className="truncate text-xs font-semibold text-foreground-800" title={row.value}>
                {row.value}
              </p>
            </div>
            {!row.stated ? (
              <span
                className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-700"
                title="Not stated in the copy — using the default for this plan type"
              >
                <i className="ri-alert-line text-[11px]" />
              </span>
            ) : null}
          </li>
        ))}
      </ul>

      {notStated.length > 0 ? (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line text-sm text-accent-700" />
          </span>
          <p className="text-[11px] leading-relaxed text-accent-900">
            Not stated in the plan copy: <span className="font-semibold">{notStated.join(", ")}</span>.
            These fall back to the default for this plan type. Add a line like{" "}
            <span className="font-medium">“List in 3 Main Categories”</span>,{" "}
            <span className="font-medium">“Up to 10 photos”</span> or{" "}
            <span className="font-medium">“Add a video tour”</span> to make it explicit.
          </p>
        </div>
      ) : (
        <p className="mt-3 flex items-center gap-1.5 text-[11px] text-emerald-700">
          <i className="ri-checkbox-circle-line text-sm" />
          Every allowance is spelled out in the copy — the form will match it exactly.
        </p>
      )}
    </div>
  );
}